import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    addIbkrConnection,
    getConnections,
    getIndexAccount,
    removeConnection,
    setConnectionNickname,
    setIndexAccount,
    startFidelityLink,
} from '../../data/portfolio';
import { useT } from '../../i18n';

// Linked accounts.
//
// The two brokers link in genuinely different ways and the UI says so rather
// than pretending they are the same: IBKR takes a query id and token you
// generate yourself, Fidelity goes out to SnapTrade's hosted portal because it
// has no public API. Hiding that difference behind one "Connect" button would
// make the IBKR flow look broken when it asks for two values.

const PLATFORM_LABEL = { ibkr: 'Interactive Brokers', fidelity: 'Fidelity' };

/** An account number is not a secret, but it is not something to leave sitting
 *  on a screen either. Enough to tell two accounts apart, not enough to quote. */
const maskAccount = (id) => (!id || id.length <= 5 ? id : `${id.slice(0, 2)}…${id.slice(-4)}`);

export default function HoldingsSettings() {
    const { t } = useT();
    const location = useLocation();
    const navigate = useNavigate();
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({ label: 'Interactive Brokers', queryId: '', token: '' });
    const [submitting, setSubmitting] = useState(false);
    const [linking, setLinking] = useState(false);
    // Held as a catalogue KEY, not a finished sentence: a notice raised before
    // the reader switches language would otherwise stay in the old one.
    const [notice, setNotice] = useState(null);
    // Which connection is being renamed, and the draft name. Nicknames are
    // org-wide, so this reloads from the server rather than patching locally.
    const [renaming, setRenaming] = useState(null);
    const [nickname, setNickname] = useState('');
    // Which account the Kanosei Index is measured on. Null while loading, and
    // when the server has nothing to offer — no IBKR account linked yet.
    const [index, setIndex] = useState(null);
    const [savingIndex, setSavingIndex] = useState(false);

    const load = useCallback(async () => {
        try {
            // Settled rather than awaited together: the index designation is
            // served off the portfolio snapshot, and a broker outage there must
            // not take the connections list — the page you fix it from — with
            // it.
            const [conns, indexAccount] = await Promise.allSettled([
                getConnections(),
                getIndexAccount(),
            ]);
            if (conns.status === 'rejected') throw conns.reason;
            setConnections(conns.value);
            setIndex(indexAccount.status === 'fulfilled' ? indexAccount.value : null);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // SnapTrade sends the browser back here with ?linked=fidelity once the
    // portal is done. Acknowledge it and strip the parameter, so a refresh does
    // not re-announce a link that happened minutes ago.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('linked') !== 'fidelity') return;
        setNotice('settings.fidelityLinkedNotice');
        navigate('/holdings/settings', { replace: true });
    }, [location.search, navigate]);

    const submit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        setNotice(null);
        try {
            await addIbkrConnection(form);
            setForm({ label: 'Interactive Brokers', queryId: '', token: '' });
            setNotice('settings.linkedNotice');
            await load();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const linkFidelity = async () => {
        setLinking(true);
        setError(null);
        setNotice(null);
        try {
            const { url } = await startFidelityLink();
            // Same tab rather than a popup: SnapTrade redirects back here when
            // it is done, and a blocked popup would look like a broken button.
            window.location.assign(url);
        } catch (err) {
            setError(err.message);
            setLinking(false);
        }
    };

    const saveNickname = async (id) => {
        setError(null);
        try {
            await setConnectionNickname(id, nickname.trim());
            setRenaming(null);
            await load();
        } catch (err) {
            setError(err.message);
        }
    };

    const chooseIndexAccount = async (accountId) => {
        setError(null);
        setSavingIndex(true);
        try {
            await setIndexAccount(accountId || null);
            setNotice('settings.indexSavedNotice');
            await load();
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingIndex(false);
        }
    };

    const unlink = async (id) => {
        setError(null);
        try {
            await removeConnection(id);
            await load();
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <section className="holdings__sec">
            <div className="wrapper">
                <div className="holdings__header">
                    <h2 className="heading styled">{t('settings.title')}</h2>
                    <div className="holdings__user">
                        <Link to="/holdings" className="holdings__settings_link">
                            {t('common.backToPortfolio')}
                        </Link>
                    </div>
                </div>

                {error && <p className="holdings__error" role="alert">{error}</p>}
                {notice && <p className="settings__notice">{t(notice)}</p>}

                {loading ? (
                    <p className="desc">{t('settings.loading')}</p>
                ) : (
                    <ul className="settings__connections">
                        {connections.length === 0 && (
                            <li className="settings__empty">{t('settings.empty')}</li>
                        )}
                        {connections.map((conn) => (
                            <li key={conn.id} className="settings__connection">
                                <div className="settings__connection_main">
                                    {renaming === conn.id ? (
                                        <form
                                            className="settings__rename"
                                            onSubmit={(e) => { e.preventDefault(); saveNickname(conn.id); }}
                                        >
                                            <input
                                                type="text"
                                                maxLength={60}
                                                value={nickname}
                                                autoFocus
                                                placeholder={conn.label}
                                                onChange={(e) => setNickname(e.target.value)}
                                                aria-label={t('settings.nicknameAria')}
                                            />
                                            <button type="submit" className="settings__rename_go">
                                                {t('common.save')}
                                            </button>
                                            <button
                                                type="button"
                                                className="settings__rename_go"
                                                onClick={() => setRenaming(null)}
                                            >
                                                {t('common.cancel')}
                                            </button>
                                        </form>
                                    ) : (
                                        <span className="settings__connection_label">
                                            {conn.nickname || conn.label}
                                            <button
                                                type="button"
                                                className="settings__rename_open"
                                                onClick={() => { setRenaming(conn.id); setNickname(conn.nickname || ''); }}
                                            >
                                                {t('settings.rename')}
                                            </button>
                                        </span>
                                    )}
                                    <span className="settings__connection_meta">
                                        {PLATFORM_LABEL[conn.source] || conn.source}
                                        {conn.nickname && ` · ${conn.label}`}
                                        {conn.reference &&
                                            ` · ${t('settings.query', { ref: conn.reference })}`}
                                        {conn.status !== 'linked' && ` · ${conn.status}`}
                                    </span>
                                    {conn.lastError && (
                                        <span className="settings__connection_error">{conn.lastError}</span>
                                    )}
                                </div>
                                {conn.managed ? (
                                    // Removing it here would appear to work and then reappear
                                    // on the next cold start, which is worse than refusing.
                                    <span
                                        className="settings__managed"
                                        title={t('settings.managedTitle')}
                                    >
                                        {t('settings.managed')}
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        className="settings__unlink"
                                        onClick={() => unlink(conn.id)}
                                    >
                                        {t('settings.unlink')}
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}

                {/* Which account the index is. This is a decision, not a
                    property of a connection: one Flex query can cover several
                    accounts, and until it is made the server falls back to
                    whichever account's holdings look like the index — which is
                    a good guess and still a guess. */}
                {index?.accounts?.some((a) => a.source === 'ibkr') && (
                    <div className="settings__add">
                        <h3 className="settings__subheading">{t('settings.indexAccount')}</h3>
                        <p className="desc">{t('settings.indexAccountHelp')}</p>
                        <label className="settings__field">
                            <span>{t('settings.indexAccountLabel')}</span>
                            <select
                                value={index.configured ?? ''}
                                disabled={savingIndex}
                                onChange={(e) => chooseIndexAccount(e.target.value)}
                            >
                                <option value="">{t('settings.indexAccountAuto')}</option>
                                {index.accounts
                                    .filter((a) => a.source === 'ibkr' && a.accountId)
                                    .map((a) => (
                                        <option key={a.accountId} value={a.accountId}>
                                            {a.label} · {maskAccount(a.accountId)}
                                        </option>
                                    ))}
                            </select>
                        </label>
                        {!index.configured && index.accountId && (
                            <p className="settings__notice">
                                {t('settings.indexAccountDerived', {
                                    account: maskAccount(index.accountId),
                                })}
                            </p>
                        )}
                    </div>
                )}

                <div className="settings__add">
                    <h3 className="settings__subheading">{t('settings.linkIbkr')}</h3>
                    <p className="desc">
                        {t('settings.ibkrBefore')}
                        <strong>{t('settings.ibkrPath')}</strong>
                        {t('settings.ibkrAfter')}
                    </p>

                    <form className="settings__form" onSubmit={submit}>
                        <label className="settings__field">
                            <span>{t('settings.label')}</span>
                            <input
                                type="text"
                                value={form.label}
                                onChange={(e) => setForm({ ...form, label: e.target.value })}
                                required
                            />
                        </label>
                        <label className="settings__field">
                            <span>{t('settings.queryId')}</span>
                            <input
                                type="text"
                                inputMode="numeric"
                                placeholder="1234567"
                                value={form.queryId}
                                onChange={(e) => setForm({ ...form, queryId: e.target.value })}
                                required
                            />
                        </label>
                        <label className="settings__field">
                            <span>{t('settings.token')}</span>
                            {/* type=password so it is not left on screen; it is stored
                                encrypted and never returned by any endpoint. */}
                            <input
                                type="password"
                                autoComplete="off"
                                value={form.token}
                                onChange={(e) => setForm({ ...form, token: e.target.value })}
                                required
                            />
                        </label>
                        <button type="submit" className="css_btn" disabled={submitting}>
                            <span>
                                {submitting ? t('settings.linking') : t('settings.linkAccount')}
                            </span>
                        </button>
                    </form>
                </div>

                <div className="settings__add">
                    <h3 className="settings__subheading">{t('settings.linkFidelity')}</h3>
                    <p className="desc">
                        {t('settings.fidelityBefore')}
                        <strong>{t('settings.fidelityBrand')}</strong>
                        {t('settings.fidelityAfter')}
                    </p>
                    <button
                        type="button"
                        className="css_btn"
                        onClick={linkFidelity}
                        disabled={linking}
                    >
                        <span>
                            {linking ? t('settings.opening') : t('settings.connectFidelity')}
                        </span>
                    </button>
                </div>
            </div>
        </section>
    );
}
